import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, Spinner } from "@shopify/polaris";
import nlTranslations from "@shopify/polaris/locales/nl.json";
import enTranslations from "@shopify/polaris/locales/en.json";
import deTranslations from "@shopify/polaris/locales/de.json";
import { ShopProvider, useShop } from "./context/ShopContext.jsx";
import { I18nProvider, useI18n } from "./context/I18nContext.jsx";
import { AdminAuthProvider, useAdminAuth } from "./context/AdminAuthContext.jsx";
import AppLayout from "./components/AppLayout.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import IntegrationPage from "./pages/IntegrationPage.jsx";
import ProductFittingPage from "./pages/ProductFittingPage.jsx";
import LandingPageEditorPage from "./pages/LandingPageEditorPage.jsx";
import LandingPageTemplatePage from "./pages/LandingPageTemplatePage.jsx";
import SessionsPage from "./pages/SessionsPage.jsx";
import AnalyticsPage from "./pages/AnalyticsPage.jsx";
import AiSettingsPage from "./pages/AiSettingsPage.jsx";
import CustomersPage from "./pages/CustomersPage.jsx";
import BrandingPage from "./pages/BrandingPage.jsx";
import SettingsPage from "./pages/SettingsPage.jsx";
import SizeChartPage from "./pages/SizeChartPage.jsx";
import TryOnPage from "./pages/TryOnPage.jsx";
import ShowroomPage from "./pages/ShowroomPage.jsx";
import ShowroomLinkPage from "./pages/ShowroomLinkPage.jsx";
import CustomerLandingPage from "./pages/CustomerLandingPage.jsx";
import CustomerDashboardPage from "./pages/CustomerDashboardPage.jsx";
import CustomerSessionDetailPage from "./pages/CustomerSessionDetailPage.jsx";
import PrivacyPage from "./pages/PrivacyPage.jsx";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage.jsx";
import TermsPage from "./pages/TermsPage.jsx";

const POLARIS_TRANSLATIONS = { nl: nlTranslations, en: enTranslations, de: deTranslations };

function PolarisWrapper({ children }) {
  const { lang } = useI18n();
  return <AppProvider i18n={POLARIS_TRANSLATIONS[lang] || nlTranslations}>{children}</AppProvider>;
}

// Syncs I18n language whenever the active shop changes
function ShopLanguageSync({ children }) {
  const { shopDomain } = useShop();
  const { loadShopLanguage } = useI18n();
  useEffect(() => {
    if (shopDomain) loadShopLanguage(shopDomain);
  }, [shopDomain, loadShopLanguage]);
  return children;
}

// Gate: show login or admin content
function AdminGate() {
  const { admin, loading } = useAdminAuth();

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner size="large" />
      </div>
    );
  }

  if (!admin) {
    return <LoginPage />;
  }

  return (
    <ShopProvider>
      <ShopLanguageSync>
        <AppLayout>
          <Routes>
            <Route path="/" element={<Navigate to="/integration" replace />} />
            <Route path="/integration" element={<IntegrationPage />} />
            <Route path="/products" element={<ProductFittingPage />} />
            <Route path="/products/:productId/landing-page" element={<LandingPageEditorPage />} />
            <Route path="/size-charts" element={<SizeChartPage />} />
            <Route path="/showroom-link" element={<ShowroomLinkPage />} />
            <Route path="/landing-page-template" element={<LandingPageTemplatePage />} />
            <Route path="/sessions" element={<SessionsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/branding" element={<BrandingPage />} />
            <Route path="/ai-settings" element={<AiSettingsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </AppLayout>
      </ShopLanguageSync>
    </ShopProvider>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <PolarisWrapper>
        <BrowserRouter>
          <AdminAuthProvider>
            <Routes>
              {/* Public pages: no admin layout */}
              <Route path="/try-on" element={<TryOnPage />} />
              <Route path="/showroom" element={<ShowroomPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/algemene-voorwaarden" element={<TermsPage />} />
              <Route path="/landing/:token" element={<CustomerLandingPage />} />
              <Route path="/my-outfits" element={<CustomerDashboardPage />} />
              <Route path="/my-outfits/:sessionId" element={<CustomerSessionDetailPage />} />

              {/* Admin pages: behind login gate */}
              <Route path="/*" element={<AdminGate />} />
            </Routes>
          </AdminAuthProvider>
        </BrowserRouter>
      </PolarisWrapper>
    </I18nProvider>
  );
}
